import React from "react";
import { Alert, Form } from "react-bootstrap";
import { OrbitControls } from "thirdparty/OrbitalControls";
import * as THREE from "three";
import styles from "./ReviewAnimationRenderer.module.scss";

interface Props {
    videoJob: sage.model.IVideoJob;
}

const linePairsLeft = [
    ["left.wrist", "left.pinky.1"],
    ["left.pinky.1", "left.pinky.2"],
    ["left.pinky.2", "left.pinky.3"],
    ["left.pinky.3", "left.pinky.4"],
    ["left.wrist", "left.ring.1"],
    ["left.ring.1", "left.ring.2"],
    ["left.ring.2", "left.ring.3"],
    ["left.ring.3", "left.ring.4"],
    ["left.wrist", "left.middle.1"],
    ["left.middle.1", "left.middle.2"],
    ["left.middle.2", "left.middle.3"],
    ["left.middle.3", "left.middle.4"],
    ["left.wrist", "left.index.1"],
    ["left.index.1", "left.index.2"],
    ["left.index.2", "left.index.3"],
    ["left.index.3", "left.index.4"],
    ["left.wrist", "left.thumb.1"],
    ["left.thumb.1", "left.thumb.2"],
    ["left.thumb.2", "left.thumb.3"],
    ["left.thumb.3", "left.thumb.4"],
];

const linePairsRight = [
    ["right.wrist", "right.pinky.1"],
    ["right.pinky.1", "right.pinky.2"],
    ["right.pinky.2", "right.pinky.3"],
    ["right.pinky.3", "right.pinky.4"],
    ["right.wrist", "right.ring.1"],
    ["right.ring.1", "right.ring.2"],
    ["right.ring.2", "right.ring.3"],
    ["right.ring.3", "right.ring.4"],
    ["right.wrist", "right.middle.1"],
    ["right.middle.1", "right.middle.2"],
    ["right.middle.2", "right.middle.3"],
    ["right.middle.3", "right.middle.4"],
    ["right.wrist", "right.index.1"],
    ["right.index.1", "right.index.2"],
    ["right.index.2", "right.index.3"],
    ["right.index.3", "right.index.4"],
    ["right.wrist", "right.thumb.1"],
    ["right.thumb.1", "right.thumb.2"],
    ["right.thumb.2", "right.thumb.3"],
    ["right.thumb.3", "right.thumb.4"],
];

const leftHandFingers = [
    "left.wrist",
    "left.pinky.1",
    "left.index.1",
    "left.middle.1",
    "left.ring.1",
    "left.thumb.1",
    "left.thumb.2",
    "left.index.2",
    "left.middle.2",
    "left.ring.2",
    "left.pinky.2",
    "left.thumb.3",
    "left.index.3",
    "left.middle.3",
    "left.ring.3",
    "left.pinky.3",
    "left.thumb.4",
    "left.index.4",
    "left.middle.4",
    "left.ring.4",
    "left.pinky.4",
];

const rightHandFingers = [
    "right.wrist",
    "right.pinky.1",
    "right.index.1",
    "right.middle.1",
    "right.ring.1",
    "right.thumb.1",
    "right.thumb.2",
    "right.index.2",
    "right.middle.2",
    "right.ring.2",
    "right.pinky.2",
    "right.thumb.3",
    "right.index.3",
    "right.middle.3",
    "right.ring.3",
    "right.pinky.3",
    "right.thumb.4",
    "right.index.4",
    "right.middle.4",
    "right.ring.4",
    "right.pinky.4",
];

const ReviewAnimationRenderer: React.FC<Props> = ({ videoJob }) => {
    const renderDiv = React.useRef<HTMLDivElement>(null);
    const [scene, setScene] = React.useState<THREE.Scene | null>(null);
    const [frame, setFrame] = React.useState(0);

    const totalFrames = React.useMemo(() => {
        const rendahandFileData = videoJob.rendahand_file_data;
        if (rendahandFileData) {
            return (
                rendahandFileData.invalid_frames +
                rendahandFileData.valid_frames
            );
        } else {
            return 0;
        }
    }, [videoJob]);

    React.useEffect(() => {
        if (!renderDiv.current) {
            return;
        }
        const width = renderDiv.current.clientWidth;
        const height = renderDiv.current.clientHeight;
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(
            75,
            width / height,
            0.1,
            1000
        );
        const renderer = new THREE.WebGLRenderer();
        camera.position.z = 5;
        const controls = new OrbitControls(camera, renderer.domElement);
        renderer.render(scene, camera);
        controls.update();
        renderer.setSize(width, height);
        renderer.setClearColor(0xffffff); // white background - replace ffffff with any hex color
        renderDiv.current.innerHTML = "";
        renderDiv.current.appendChild(renderer.domElement);

        const animate = function () {
            requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };

        setScene(scene);
        animate();
    }, [renderDiv, videoJob, setScene]);

    React.useEffect(() => {
        const rendahandFileData = videoJob.rendahand_file_data;
        if (!rendahandFileData || !scene) {
            return;
        }

        const material = new THREE.MeshBasicMaterial({ color: 0x4657ec });

        const frameData = rendahandFileData.display_results[frame];

        if (!frameData) {
            return;
        }

        const fingers =
            rendahandFileData.type === "LEFT_HAND"
                ? leftHandFingers
                : rightHandFingers;

        const scaleFactor = 500;

        for (const finger of fingers) {
            const currFrameData = frameData[finger];
            const adjustedX = currFrameData[0] / scaleFactor;
            const adjustedY = currFrameData[1] / scaleFactor;
            const adjustedZ = currFrameData[2] / scaleFactor;
            const obj = scene.getObjectByName(finger) as THREE.Mesh;
            if (obj) {
                obj.position.x = adjustedX;
                obj.position.y = adjustedY;
                obj.position.z = adjustedZ;
            } else {
                const newGeom = new THREE.SphereGeometry(0.07);
                const circle = new THREE.Mesh(newGeom, material);
                circle.position.x = adjustedX;
                circle.position.y = adjustedY;
                circle.position.z = adjustedZ;
                circle.name = finger;
                scene.add(circle);
            }
        }

        const lineMaterial = new THREE.LineBasicMaterial({
            color: 0xadb5bd,
        });

        const lines =
            rendahandFileData.type === "LEFT_HAND"
                ? linePairsLeft
                : linePairsRight;

        lines.forEach((line) => {
            const startFinger = line[0];
            const endFinger = line[1];

            const startPoint = frameData[startFinger];
            const endPoint = frameData[endFinger];

            const currPoint = [
                new THREE.Vector3(
                    startPoint[0] / scaleFactor,
                    startPoint[1] / scaleFactor,
                    startPoint[2] / scaleFactor
                ),
                new THREE.Vector3(
                    endPoint[0] / scaleFactor,
                    endPoint[1] / scaleFactor,
                    endPoint[2] / scaleFactor
                ),
            ];

            const name = startFinger + endFinger;

            const geometry = new THREE.BufferGeometry().setFromPoints(
                currPoint
            );

            const obj = scene.getObjectByName(name) as THREE.Line;
            if (obj) {
                obj.geometry = geometry;
            } else {
                const lineItem = new THREE.Line(geometry, lineMaterial);
                lineItem.name = name;
                scene.add(lineItem);
            }
        });
    }, [scene, frame, videoJob.rendahand_file_data]);

    const onFrameChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setFrame(parseInt(e.target.value));
        },
        [setFrame]
    );

    const hasNoData = React.useMemo(() => {
        return (
            !videoJob ||
            !videoJob.rendahand_file_data ||
            !videoJob.rendahand_file_data.display_results[frame]
        );
    }, [videoJob, frame]);

    return (
        <div className={styles.outerContainer}>
            <div className={styles.rendererBox} ref={renderDiv}></div>
            <Form>
                <Form.Group>
                    <Form.Label>Frame {frame}</Form.Label>
                    <Form.Control
                        max={totalFrames - 1}
                        min={0}
                        value={frame}
                        onChange={onFrameChange}
                        type="range"
                        custom
                    />
                </Form.Group>
            </Form>
            {hasNoData && (
                <Alert variant="warning">
                    This frame does not have any data. Don&apos;t worry, if most
                    frames still have data, your animation will still look good.
                </Alert>
            )}
        </div>
    );
};

export default ReviewAnimationRenderer;
